import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { subHours, subDays, format } from 'date-fns';

const AlertContext = createContext();
const API_URL = process.env.REACT_APP_BACKEND_URL;

export const useAlerts = () => {
  const context = useContext(AlertContext);
  if (!context) {
    throw new Error('useAlerts must be used within an AlertProvider');
  }
  return context;
};

// Configurações padrão para alertas
const defaultAlertSettings = {
  energia: {
    enabled: true,
    desvioConsumo: 15,
    consumoMaximo: 600,
  },
  fotovoltaico: {
    enabled: true,
    producaoMinima: 100,
    eficienciaMinima: 20,
  },
  agua: {
    enabled: true,
    desvioCaudal: 20, // % de desvio do consumo médio
    consumoMaximo: 5, // m³ por dia
    diasSemLeitura: 2, // alerta se não houver leitura em X dias
  },
  elevadores: {
    enabled: true,
    tempoAvaria: 30,
    tempoParado: 120,
  },
  postosVE: {
    enabled: true,
    tempoInativo: 24,
    falhasConsecutivas: 3,
  },
};

// Gerar alertas simulados para categorias sem dados reais
const generateMockAlerts = (settings) => {
  const alerts = [];
  const now = new Date();

  // Alertas de Energia (simulados)
  if (settings.energia.enabled) {
    alerts.push({
      id: 'energia-1',
      categoria: 'energia',
      tipo: 'Desvio de Consumo',
      mensagem: `Desvio de consumo de ${settings.energia.desvioConsumo + 3}% detectado em Pragal`,
      local: 'Pragal',
      valor: `${settings.energia.desvioConsumo + 3}%`,
      limiar: `${settings.energia.desvioConsumo}%`,
      timestamp: subHours(now, 2),
      lido: false,
      severidade: 'warning',
      simulado: true,
    });
  }

  // Alertas de Fotovoltaico (simulados)
  if (settings.fotovoltaico.enabled) {
    alerts.push({
      id: 'fotovoltaico-1',
      categoria: 'fotovoltaico',
      tipo: 'Produção Baixa',
      mensagem: `Produção de apenas ${settings.fotovoltaico.producaoMinima - 30} kWh em Évora`,
      local: 'Évora',
      valor: `${settings.fotovoltaico.producaoMinima - 30} kWh`,
      limiar: `${settings.fotovoltaico.producaoMinima} kWh`,
      timestamp: subHours(now, 8),
      lido: true,
      severidade: 'warning',
      simulado: true,
    });
  }

  // Alertas de Elevadores (simulados)
  if (settings.elevadores.enabled) {
    alerts.push({
      id: 'elevadores-1',
      categoria: 'elevadores',
      tipo: 'Avaria Prolongada',
      mensagem: `Elevador E2 avariado há ${settings.elevadores.tempoAvaria + 15} minutos em Pragal`,
      local: 'Pragal',
      valor: `${settings.elevadores.tempoAvaria + 15} min`,
      limiar: `${settings.elevadores.tempoAvaria} min`,
      timestamp: subHours(now, 1),
      lido: false,
      severidade: 'critical',
      simulado: true,
    });
  }

  // Alertas de Postos VE (simulados)
  if (settings.postosVE.enabled) {
    alerts.push({
      id: 'postosVE-1',
      categoria: 'postosVE',
      tipo: 'Posto Inativo',
      mensagem: `Posto P003 inativo há ${settings.postosVE.tempoInativo + 12} horas`,
      local: 'Évora',
      valor: `${settings.postosVE.tempoInativo + 12}h`,
      limiar: `${settings.postosVE.tempoInativo}h`,
      timestamp: subDays(now, 1),
      lido: false,
      severidade: 'warning',
      simulado: true,
    });
  }

  return alerts;
};

// Gerar alertas de água baseados em dados REAIS
const generateWaterAlertsFromRealData = (settings, consumosData, deviceConfigs) => {
  const alerts = [];
  const now = new Date();

  if (!settings.agua.enabled || !consumosData || Object.keys(consumosData).length === 0) {
    return alerts;
  }

  Object.entries(consumosData).forEach(([deviceId, deviceData]) => {
    const config = deviceConfigs.find(c => c.device_id === deviceId);
    const deviceName = config?.descricao || deviceId;
    const local = config?.local || 'N/A';

    if (!deviceData.consumos || deviceData.consumos.length === 0) {
      // Alerta: Sem leituras
      alerts.push({
        id: `agua-sem-leitura-${deviceId}`,
        categoria: 'agua',
        tipo: 'Sem Leituras',
        mensagem: `${deviceName} sem leituras recentes`,
        local: local,
        valor: '0 leituras',
        limiar: `>${settings.agua.diasSemLeitura} dias`,
        timestamp: now,
        lido: false,
        severidade: 'critical',
        simulado: false,
        dispositivo: deviceName,
      });
      return;
    }

    // Calcular consumo diário
    const dailyConsumo = {};
    deviceData.consumos.forEach(consumo => {
      const date = consumo.timestamp.split('T')[0];
      if (!dailyConsumo[date]) {
        dailyConsumo[date] = 0;
      }
      dailyConsumo[date] += (consumo.valor_litros || 0) / 1000; // Converter para m³
    });

    const dailyValues = Object.entries(dailyConsumo).sort((a, b) => b[0].localeCompare(a[0]));
    
    if (dailyValues.length === 0) return;

    // Calcular média de consumo
    const values = dailyValues.map(([_, v]) => v);
    const avgConsumo = values.reduce((a, b) => a + b, 0) / values.length;

    // Verificar os últimos dias para desvios
    dailyValues.slice(0, 7).forEach(([date, consumo]) => {
      // 1. Alerta de desvio de consumo
      if (avgConsumo > 0) {
        const desvio = ((consumo - avgConsumo) / avgConsumo) * 100;
        
        if (Math.abs(desvio) > settings.agua.desvioCaudal) {
          const severidade = desvio > 0 ? 'warning' : 'info';
          const tipo = desvio > 0 ? 'Consumo Acima da Média' : 'Consumo Abaixo da Média';
          
          alerts.push({
            id: `agua-desvio-${deviceId}-${date}`,
            categoria: 'agua',
            tipo: tipo,
            mensagem: `${deviceName}: desvio de ${desvio > 0 ? '+' : ''}${desvio.toFixed(1)}% no dia ${format(new Date(date), 'dd/MM')}`,
            local: local,
            valor: `${consumo.toFixed(3)} m³`,
            limiar: `Média: ${avgConsumo.toFixed(3)} m³`,
            timestamp: new Date(date),
            lido: false,
            severidade: severidade,
            simulado: false,
            dispositivo: deviceName,
            desvioPercent: desvio.toFixed(1),
          });
        }
      }

      // 2. Alerta de consumo máximo diário
      if (consumo > settings.agua.consumoMaximo) {
        alerts.push({
          id: `agua-max-${deviceId}-${date}`,
          categoria: 'agua',
          tipo: 'Consumo Elevado',
          mensagem: `${deviceName}: consumo de ${consumo.toFixed(3)} m³ excede limite no dia ${format(new Date(date), 'dd/MM')}`,
          local: local,
          valor: `${consumo.toFixed(3)} m³`,
          limiar: `${settings.agua.consumoMaximo} m³`,
          timestamp: new Date(date),
          lido: false,
          severidade: 'critical',
          simulado: false,
          dispositivo: deviceName,
        });
      }

      // 3. Alerta de consumo zero (possível problema no contador)
      if (consumo === 0) {
        alerts.push({
          id: `agua-zero-${deviceId}-${date}`,
          categoria: 'agua',
          tipo: 'Consumo Zero',
          mensagem: `${deviceName}: consumo zero detectado no dia ${format(new Date(date), 'dd/MM')}`,
          local: local,
          valor: '0 m³',
          limiar: '>0 m³',
          timestamp: new Date(date),
          lido: false,
          severidade: 'info',
          simulado: false,
          dispositivo: deviceName,
        });
      }
    });
  });

  return alerts;
};

export const AlertProvider = ({ children }) => {
  const { user } = useAuth();
  const [alertSettings, setAlertSettings] = useState(defaultAlertSettings);
  const [alerts, setAlerts] = useState([]);
  const [waterData, setWaterData] = useState({ consumos: {}, configs: [] });
  const [isLoadingWater, setIsLoadingWater] = useState(false);

  // Carregar dados de água da API
  const loadWaterData = useCallback(async () => {
    setIsLoadingWater(true);
    try {
      // Verificar conexão à API
      const testResponse = await fetch(`${API_URL}/api/agua/test-connection`);
      const testData = await testResponse.json();

      if (!testData.success) {
        setIsLoadingWater(false);
        return;
      }

      // Carregar configurações de dispositivos
      const configResponse = await fetch(`${API_URL}/api/configuracoes/dispositivos?categoria=agua`);
      const configs = await configResponse.json();

      // Carregar consumos de cada dispositivo (últimos 14 dias)
      const consumosPromises = (configs || []).map(async (config) => {
        const response = await fetch(`${API_URL}/api/agua/consumos/${config.device_id}?dias=14`);
        const data = await response.json();
        return { deviceId: config.device_id, ...data };
      });

      const consumosResults = await Promise.all(consumosPromises);
      const consumosMap = {};
      consumosResults.forEach(result => {
        consumosMap[result.deviceId] = result;
      });

      setWaterData({ consumos: consumosMap, configs: configs || [] });
    } catch (error) {
      console.error('Erro ao carregar dados de água para alertas:', error);
    }
    setIsLoadingWater(false);
  }, []);

  // Carregar configurações do localStorage quando o utilizador muda
  useEffect(() => {
    if (user?.email) {
      const storageKey = `alertSettings_${user.email}`;
      const savedSettings = localStorage.getItem(storageKey);
      if (savedSettings) {
        setAlertSettings(JSON.parse(savedSettings));
      } else {
        setAlertSettings(defaultAlertSettings);
      }
    }
  }, [user?.email]);

  // Carregar dados de água ao iniciar
  useEffect(() => {
    loadWaterData();
    // Atualizar a cada 15 minutos
    const interval = setInterval(loadWaterData, 15 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadWaterData]);

  // Gerar alertas quando as configurações ou dados mudam
  useEffect(() => {
    // Alertas simulados para categorias sem dados reais
    const mockAlerts = generateMockAlerts(alertSettings);
    
    // Alertas REAIS de água
    const waterAlerts = generateWaterAlertsFromRealData(
      alertSettings,
      waterData.consumos,
      waterData.configs
    );

    // Combinar e ordenar por data
    const allAlerts = [...mockAlerts, ...waterAlerts]
      .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
    
    setAlerts(allAlerts);
  }, [alertSettings, waterData]);

  // Guardar configurações no localStorage
  const saveAlertSettings = (newSettings) => {
    if (user?.email) {
      const storageKey = `alertSettings_${user.email}`;
      localStorage.setItem(storageKey, JSON.stringify(newSettings));
      setAlertSettings(newSettings);
    }
  };

  // Marcar alerta como lido
  const markAsRead = (alertId) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, lido: true } : alert
    ));
  };

  // Marcar todos como lidos
  const markAllAsRead = () => {
    setAlerts(prev => prev.map(alert => ({ ...alert, lido: true })));
  };

  // Forçar atualização dos dados de água
  const refreshWaterAlerts = () => {
    loadWaterData();
  };

  // Contar alertas não lidos
  const unreadCount = alerts.filter(alert => !alert.lido).length;

  // Contar alertas reais vs simulados
  const realAlertsCount = alerts.filter(alert => !alert.simulado).length;
  const simulatedAlertsCount = alerts.filter(alert => alert.simulado).length;

  // Agrupar alertas por categoria
  const alertsByCategory = alerts.reduce((acc, alert) => {
    if (!acc[alert.categoria]) {
      acc[alert.categoria] = [];
    }
    acc[alert.categoria].push(alert);
    return acc;
  }, {});

  return (
    <AlertContext.Provider value={{
      alertSettings,
      saveAlertSettings,
      alerts,
      alertsByCategory,
      unreadCount,
      realAlertsCount,
      simulatedAlertsCount,
      markAsRead,
      markAllAsRead,
      refreshWaterAlerts,
      isLoadingWater,
    }}>
      {children}
    </AlertContext.Provider>
  );
};
